import React from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import agent from "../../../agent";
import {
  buildPreviewSummary,
  buildCommitSummary,
  buildReplacementTable,
  MILE_NAMES,
  REPLACEMENT_ACTIONS,
} from "./importAdapters";
import ProbeTable from "./ProbeTable";

/**
 * Заливка тарифов перевозчика из файла.
 *
 * Шаги идут строго по порядку: выбрать пару «перевозчик — услуга», выбрать файл,
 * посмотреть, что в нём нашлось и что станет с тарифом услуги, и только потом
 * подтвердить. Заливка ЗАМЕЩАЕТ тариф пары целиком в своей области: без галочки
 * — первую милю, магистраль и последнюю милю, с галочкой — только внутригород.
 */
const CarrierTariffs = () => {
  const [carriers, setCarriers] = React.useState([]);
  const [carrier, setCarrier] = React.useState(null);
  const [intracityOnly, setIntracityOnly] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const [stage, setStage] = React.useState("idle");
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    agent.OrgTariffs.postalServices()
      .then((options) =>
        setCarriers(
          (options ?? []).map((option) => ({
            ...option,
            key: `${option.psCode}|${option.psTypeCode}`,
            label: `${option.psCode} — ${option.psTypeCode}${option.name ? ` (${option.name})` : ""}`,
          }))
        )
      )
      .catch(() => setError("Не удалось загрузить список перевозчиков"));
  }, []);

  // Смена перевозчика или файла обесценивает и предпросмотр, и отчёт:
  // подтверждать можно только то, что показали.
  const reset = () => {
    setPreview(null);
    setReport(null);
    setError(null);
    setStage("idle");
  };

  const handleCarrier = (value) => {
    setCarrier(value);
    reset();
  };

  const handleFile = (event) => {
    setFile(event.target.files?.[0] ?? null);
    reset();
  };

  const handleIntracity = (checked) => {
    setIntracityOnly(checked);
    reset();
  };

  const runPreview = () => {
    setStage("previewing");
    setError(null);
    agent.CarrierTariffs.importPreview(file, intracityOnly, carrier.psId)
      .then((response) => {
        setPreview(response);
        setStage("preview");
      })
      .catch((e) => {
        setError(readError(e, "Не удалось разобрать файл"));
        setStage("idle");
      });
  };

  const runCommit = () => {
    setStage("committing");
    setError(null);
    agent.CarrierTariffs.importCommit({
      file,
      psId: carrier.psId,
      intracityOnly,
      fingerprint: preview.fingerprint,
    })
      .then((response) => {
        setReport(response.report);
        setStage("done");
      })
      .catch((e) => {
        setError(readError(e, "Заливка не выполнена"));
        setStage("preview");
      });
  };

  const summary = buildPreviewSummary(preview);
  const replacement = buildReplacementTable(preview);
  const commitSummary = buildCommitSummary(report);
  // Предпросмотр снимается по услуге: без неё не показать, что заливка удалит.
  const canPreview = Boolean(file) && Boolean(carrier?.psId) && stage !== "previewing";
  const canCommit = Boolean(carrier?.psId) && Boolean(preview?.canCommit) && stage === "preview";

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0 }}>Заливка тарифов перевозчика</h2>
      <Message
        severity="warn"
        text="Заливка замещает тариф выбранной пары «перевозчик — услуга» целиком в своей области: без галочки — первую милю, магистраль и последнюю милю (внутригород не трогается), с галочкой — только внутригород. Прежние ноги и диапазоны удаляются; копия остаётся в архиве под номером заливки."
        style={{ display: "block", marginBottom: 16 }}
      />

      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
        <label style={{ display: "grid", gap: 6, maxWidth: 520 }}>
          <span>Перевозчик и услуга</span>
          <Dropdown
            value={carrier}
            options={carriers}
            optionLabel="label"
            dataKey="key"
            filter
            placeholder="Выберите пару"
            onChange={(e) => handleCarrier(e.value)}
          />
        </label>

        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Checkbox
            inputId="intracity-only"
            checked={intracityOnly}
            onChange={(e) => handleIntracity(e.checked)}
          />
          <span>Файл только с внутригородом</span>
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Файл тарифа (.xlsx)</span>
          <input type="file" accept=".xlsx" aria-label="Файл тарифа" onChange={handleFile} />
        </label>

        <div>
          <Button label="Посмотреть содержимое" disabled={!canPreview} onClick={runPreview} />
        </div>
      </div>

      {error && <Message severity="error" text={error} style={{ display: "block", marginBottom: 16 }} />}

      {(stage === "previewing" || stage === "committing") && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}

      {preview && stage !== "done" && (
        <section style={{ marginBottom: 24 }}>
          <h3>Что нашлось в файле</h3>
          <div style={tileRow}>
            <Tile label="Точек лестницы" value={summary.ladderPoints} />
            <Tile label="Маршрутов" value={summary.routes} />
            <Tile label="Листов" value={summary.sheets} />
            <Tile label="Предупреждений" value={summary.warnings} tone={summary.warnings ? "warn" : "ok"} />
            <Tile label="Ошибок" value={summary.errors} tone={summary.errors ? "error" : "ok"} />
          </div>

          {preview.sheets?.length > 0 && (
            <ul style={{ marginTop: 12 }}>
              {preview.sheets.map((sheet) => (
                <li key={sheet.sheetName}>
                  <strong>{sheet.sheetName}</strong> ({sheet.mileType}): строк {sheet.rowsAccepted} из{" "}
                  {sheet.rowsRead}, маршрутов {sheet.routes}, точек лестницы {sheet.ladderPoints}
                </li>
              ))}
            </ul>
          )}

          <IssueList title="Ошибки разбора" issues={preview.errors} tone="error" />
          <IssueList title="Предупреждения" issues={preview.warnings} tone="warn" />

          {replacement && (
            <ReplacementTable
              replacement={replacement}
              warnings={preview.replacementWarnings}
              carrierLabel={carrier?.label}
            />
          )}

          {!preview.canCommit && (
            <Message
              severity="error"
              text="Заливка невозможна: в файле есть ошибки разбора."
              style={{ display: "block", marginTop: 12 }}
            />
          )}
          {!carrier?.psId && preview.canCommit && (
            <Message
              severity="warn"
              text="Выберите пару «перевозчик — услуга», чтобы залить тариф."
              style={{ display: "block", marginTop: 12 }}
            />
          )}

          <div style={{ marginTop: 16 }}>
            <Button
              label={
                replacement?.deletedLegs
                  ? `Залить тариф и удалить прежние ноги (${replacement.deletedLegs})`
                  : "Залить тариф"
              }
              severity="danger"
              disabled={!canCommit}
              onClick={runCommit}
            />
          </div>
        </section>
      )}

      {report && stage === "done" && (
        <section>
          <h3>Тариф залит</h3>
          <div style={tileRow}>
            {commitSummary.importId && <Tile label="Заливка №" value={commitSummary.importId} />}
            <Tile label="Строк принято" value={commitSummary.rowsApplied} />
            <Tile label="Отклонено" value={commitSummary.rejected} tone={commitSummary.rejected ? "warn" : "ok"} />
            <Tile label="Маршрутов создано" value={commitSummary.routesCreated} />
            <Tile label="Ног" value={commitSummary.legsCreated} />
            <Tile label="Диапазонов" value={commitSummary.weightRangesCreated} />
            <Tile label="Удалено ног" value={commitSummary.deletedLegs} />
            <Tile
              label="Цена падает"
              value={commitSummary.monotonicityViolations}
              tone={commitSummary.monotonicityViolations ? "error" : "ok"}
            />
            <Tile
              label="Расхождений лестницы"
              value={commitSummary.ladderInconsistencies}
              tone={commitSummary.ladderInconsistencies ? "warn" : "ok"}
            />
          </div>

          {commitSummary.monotonicityViolations > 0 && (
            <Message
              severity="error"
              text="На части весов цена падает при росте веса. Это дефект расчёта — сообщите разработчикам."
              style={{ display: "block", marginTop: 12 }}
            />
          )}
          {commitSummary.ladderInconsistencies > 0 && (
            <Message
              severity="warn"
              text="Цены точек лестницы не сходятся с надбавкой за килограмм. Противоречие в самом тарифе — уточните у перевозчика."
              style={{ display: "block", marginTop: 12 }}
            />
          )}

          <h3 style={{ marginTop: 24 }}>Контрольные точки</h3>
          <ProbeTable probe={report.probe} />
        </section>
      )}
    </div>
  );
};

/**
 * Что станет с тарифом услуги по милям. Оператор видит, сколько ног уйдёт, до
 * того как согласится: 14.10.2026 внутригородской файл молча стёр 1551 ногу
 * Казпочты P154, и на экране об этом не было ни слова.
 */
const ReplacementTable = ({ replacement, warnings, carrierLabel }) => {
  const { rows, deletedLegs, deletedWeightRanges, lastImport } = replacement;
  const cell = { padding: "4px 12px 4px 0", textAlign: "left" };
  const tone = { delete: "#b91c1c", replace: "#92400e", add: "#047857", keep: "#6b7280" };

  return (
    <div style={{ marginTop: 16 }}>
      <h3>Что станет с тарифом услуги</h3>
      {rows.length === 0 ? (
        <div>В услуге пока нет ни одной ноги.</div>
      ) : (
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={cell}>Миля</th>
              <th style={cell}>Сейчас ног</th>
              <th style={cell}>Диапазонов</th>
              <th style={cell}>Маршрутов в файле</th>
              <th style={cell}>Что будет</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.mileType}>
                <td style={cell}>{row.name}</td>
                <td style={cell}>{row.legs}</td>
                <td style={cell}>{row.weightRanges}</td>
                <td style={cell}>{row.fileRoutes}</td>
                <td style={{ ...cell, color: tone[row.action] }}>{REPLACEMENT_ACTIONS[row.action]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Message
        severity={deletedLegs ? "warn" : "info"}
        text={
          deletedLegs
            ? `Из услуги ${carrierLabel ?? ""} будет удалено ног: ${deletedLegs}, диапазонов: ${deletedWeightRanges}.`
            : "Из услуги ничего не удалится."
        }
        style={{ display: "block", marginTop: 12 }}
      />

      {warnings?.length > 0 && (
        <ul style={{ color: "#b91c1c", marginTop: 8 }}>
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      {lastImport && (
        <div style={{ color: "#6b7280", marginTop: 8 }}>
          Прошлая заливка в эту услугу: №{lastImport.id}
          {lastImport.createdAt ? ` от ${new Date(lastImport.createdAt).toLocaleString("ru-RU")}` : ""}
          {lastImport.intracityOnly ? `, ${MILE_NAMES.INTRACITY.toLowerCase()}` : ", межгород"}
          {lastImport.fileName ? `, файл «${lastImport.fileName}»` : ""}
          {lastImport.createdBy ? `, пользователь ${lastImport.createdBy}` : ""}.
        </div>
      )}
    </div>
  );
};

const IssueList = ({ title, issues, tone }) => {
  if (!issues?.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <strong>{title}</strong>
      <ul style={{ color: tone === "error" ? "#b91c1c" : "#92400e", marginTop: 4 }}>
        {issues.slice(0, 50).map((issue, index) => (
          <li key={`${issue.sheet}-${issue.row}-${index}`}>
            {issue.sheet ? `${issue.sheet}` : "файл"}
            {issue.row ? `, строка ${issue.row}` : ""}: {issue.message}
          </li>
        ))}
      </ul>
      {issues.length > 50 && <div style={{ color: "#6b7280" }}>…и ещё {issues.length - 50}</div>}
    </div>
  );
};

const Tile = ({ label, value, tone = "default" }) => {
  const palette = {
    default: { background: "#eff6ff", border: "#bfdbfe" },
    ok: { background: "#ecfdf5", border: "#a7f3d0" },
    warn: { background: "#fffbeb", border: "#fde68a" },
    error: { background: "#fef2f2", border: "#fecaca" },
  }[tone];

  return (
    <div style={{ padding: "10px 12px", borderRadius: 8, background: palette.background, border: `1px solid ${palette.border}` }}>
      <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: "1.35rem", fontWeight: 600 }}>{value}</div>
    </div>
  );
};

const tileRow = { display: "flex", flexWrap: "wrap", gap: 8 };

const readError = (e, fallback) =>
  e?.response?.body?.message || e?.response?.body?.error || e?.message || fallback;

export default CarrierTariffs;
